/* ============================================================
   CTR Main Product v2 — Gallery (grid + slider), Accordions, ATC
   ============================================================ */

if (!window.CtrMainProductV2) {

class CtrMainProductV2 {
  constructor(section) {
    this.section = section;
    this.slides = [...section.querySelectorAll('[data-ctr-main-product-v2-slide]')];
    this.thumbs = [...section.querySelectorAll('[data-ctr-main-product-v2-thumb]')];
    this.track = section.querySelector('[data-ctr-main-product-v2-track]');
    this.thumbsContainer = section.querySelector('[data-ctr-main-product-v2-thumbnails]');
    this.prevBtn = section.querySelector('[data-ctr-main-product-v2-prev]');
    this.nextBtn = section.querySelector('[data-ctr-main-product-v2-next]');
    this.mainPrevBtn = section.querySelector('[data-ctr-main-product-v2-main-prev]');
    this.mainNextBtn = section.querySelector('[data-ctr-main-product-v2-main-next]');
    this.accordions = [...section.querySelectorAll('[data-ctr-main-product-v2-accordion]')];
    this.forms = [...section.querySelectorAll('[data-ctr-main-product-v2-form]')];
    this.videoPlayBtns = [...section.querySelectorAll('[data-ctr-main-product-v2-video-play]')];
    this.videoTracks = [...section.querySelectorAll('[data-ctr-main-product-v2-video-track]')];
    this.currentIndex = 0;

    if (this.slides.length > 0) this.initGallery();
    if (this.accordions.length > 0) this.initAccordions();
    if (this.forms.length > 0) this.initForms();
    if (this.videoTracks.length > 0 || this.videoPlayBtns.length > 0) this.initVideoRow();
    if (this.section.dataset.ctrKachingEnabled) {
      this.initKachingPriceBlockSync();
      this.initKachingBuyOnceReposition();
      this.initOtpPriceSync();
    }
  }

  /* ----------------------------------------------------------------
     Gallery
     ---------------------------------------------------------------- */

  isGridMode() {
    return (
      this.section.dataset.galleryLayout === 'grid' &&
      window.innerWidth >= 768
    );
  }

  initGallery() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.goTo(parseInt(thumb.dataset.ctrMainProductV2Thumb, 10));
      });
    });

    this.mainPrevBtn?.addEventListener('click', () => {
      this.goTo(
        this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1
      );
    });

    this.mainNextBtn?.addEventListener('click', () => {
      this.goTo((this.currentIndex + 1) % this.slides.length);
    });

    this.prevBtn?.addEventListener('click', () => {
      if (!this.thumbsContainer) return;
      const amount = (this.thumbs[0]?.offsetWidth || 72) + 8;
      this.thumbsContainer.scrollBy({ left: -amount, behavior: 'smooth' });
    });

    this.nextBtn?.addEventListener('click', () => {
      if (!this.thumbsContainer) return;
      const amount = (this.thumbs[0]?.offsetWidth || 72) + 8;
      this.thumbsContainer.scrollBy({ left: amount, behavior: 'smooth' });
    });

    if (this.thumbsContainer) {
      this.thumbsContainer.addEventListener('scroll', () => this.updateThumbArrows(), { passive: true });
      this.updateThumbArrows();
    }

    window.addEventListener('resize', () => this.onResize(), { passive: true });

    this.initSwipe();
    this.initThumbDrag();
    this.initMainDrag();

    this.applyGalleryMode();
  }

  applyGalleryMode() {
    if (this.isGridMode()) {
      if (this.track) this.track.style.transform = '';
    } else {
      if (this.track) {
        this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;
      }
    }
  }

  onResize() {
    this.applyGalleryMode();
    this.updateThumbArrows();
  }

  goTo(idx) {
    const prevIdx = this.currentIndex;

    this.thumbs[prevIdx]?.classList.remove('is-active');
    this.slides[prevIdx]?.classList.remove('is-active');

    this.currentIndex = idx;

    this.slides[this.currentIndex]?.classList.add('is-active');
    this.thumbs[this.currentIndex]?.classList.add('is-active');

    if (!this.isGridMode() && this.track) {
      this.track.style.transform = `translateX(-${idx * 100}%)`;
    }

    this.thumbs[this.currentIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }

  updateThumbArrows() {
    const el = this.thumbsContainer;
    if (!el) return;
    if (this.isGridMode()) return;
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  }

  initThumbDrag() {
    const el = this.thumbsContainer;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let hasDragged = false;

    el.addEventListener('mousedown', (e) => {
      isDown = true;
      hasDragged = false;
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
      el.classList.add('is-dragging');
    });

    const end = () => {
      if (!isDown) return;
      isDown = false;
      el.classList.remove('is-dragging');
    };

    el.addEventListener('mouseleave', end);
    el.addEventListener('mouseup', end);

    el.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = x - startX;
      if (Math.abs(walk) > 4) hasDragged = true;
      el.scrollLeft = scrollLeft - walk;
    });

    el.addEventListener('click', (e) => {
      if (hasDragged) {
        e.stopPropagation();
        hasDragged = false;
      }
    }, true);
  }

  initMainDrag() {
    const gallery = this.section.querySelector('[data-ctr-main-product-v2-gallery]');
    if (!gallery || this.slides.length <= 1) return;

    let startX = 0;
    let isDragging = false;
    let hasDragged = false;

    gallery.addEventListener('mousedown', (e) => {
      startX = e.clientX;
      isDragging = true;
      hasDragged = false;
      if (this.track) this.track.style.transition = 'none';
    });

    window.addEventListener('mousemove', (e) => {
      if (!isDragging || this.isGridMode()) return;
      const diff = e.clientX - startX;
      if (Math.abs(diff) > 4) hasDragged = true;
      if (this.track) {
        this.track.style.transform = `translateX(calc(-${this.currentIndex * 100}% + ${diff}px))`;
      }
    });

    window.addEventListener('mouseup', (e) => {
      if (!isDragging) return;
      isDragging = false;
      if (this.track) this.track.style.transition = '';
      if (!hasDragged) return;

      const diff = e.clientX - startX;
      const threshold = gallery.offsetWidth * 0.2;
      if (diff < -threshold && this.currentIndex < this.slides.length - 1) {
        this.goTo(this.currentIndex + 1);
      } else if (diff > threshold && this.currentIndex > 0) {
        this.goTo(this.currentIndex - 1);
      } else {
        this.goTo(this.currentIndex);
      }
    });

    gallery.addEventListener('click', (e) => {
      if (hasDragged) e.stopPropagation();
    }, true);
  }

  initSwipe() {
    const gallery = this.section.querySelector('[data-ctr-main-product-v2-gallery]');
    if (!gallery || this.slides.length <= 1) return;

    let startX = 0;

    gallery.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    gallery.addEventListener('touchend', (e) => {
      const diff = e.changedTouches[0].clientX - startX;
      if (Math.abs(diff) < 40) return;
      if (diff < 0) {
        this.goTo((this.currentIndex + 1) % this.slides.length);
      } else {
        this.goTo((this.currentIndex - 1 + this.slides.length) % this.slides.length);
      }
    }, { passive: true });
  }

  /* ----------------------------------------------------------------
     Accordions
     ---------------------------------------------------------------- */

  initAccordions() {
    this.accordions.forEach((acc) => {
      const trigger = acc.querySelector('[data-ctr-main-product-v2-accordion-trigger]');
      const body = acc.querySelector('[data-ctr-main-product-v2-accordion-body]');
      if (!trigger || !body) return;

      if (acc.classList.contains('is-open')) {
        body.style.maxHeight = 'none';
      }

      trigger.addEventListener('click', () => {
        const isOpen = acc.classList.contains('is-open');
        if (isOpen) {
          body.style.maxHeight = body.scrollHeight + 'px';
          body.offsetHeight;
          body.style.maxHeight = '0';
          acc.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          acc.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          body.style.maxHeight = body.scrollHeight + 'px';
          body.addEventListener('transitionend', () => {
            if (acc.classList.contains('is-open')) body.style.maxHeight = 'none';
          }, { once: true });
        }
      });
    });
  }

  /* ----------------------------------------------------------------
     Add to Cart
     ---------------------------------------------------------------- */

  initForms() {
    this.forms.forEach((form) => {
      /* sync selling plan radio → hidden input */
      form.querySelectorAll('[data-ctr-main-product-v2-plan-radio]').forEach((radio) => {
        radio.addEventListener('change', () => {
          const hidden = form.querySelector('[data-ctr-main-product-v2-selling-plan]');
          if (hidden) hidden.value = radio.value;
          form.querySelectorAll('.ctr-main-product-v2__plan-option').forEach((opt) => {
            opt.classList.toggle('is-selected', opt.contains(radio) && radio.checked);
          });
        });
      });

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.handleFormSubmit(form);
      });
    });

    /* update pricing block on variant change */
    this.section.addEventListener('change', (e) => {
      const select = e.target.closest('select[name="id"], input[name="id"]');
      if (select) this.updatePricing(select.value);
    });
  }

  updatePricing(variantId) {
    const url = `${window.location.pathname}?variant=${variantId}&section_id=${this.section.dataset.sectionId}`;
    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newPricing = doc.querySelector('[data-ctr-main-product-v2-pricing]');
        const curPricing = this.section.querySelector('[data-ctr-main-product-v2-pricing]');
        if (!newPricing || !curPricing) return;

        const fields = [
          ['[data-ctr-main-product-v2-price-current]', 'textContent'],
          ['[data-ctr-main-product-v2-price-compare]', 'both'],
          ['[data-ctr-main-product-v2-price-discount]', 'both'],
        ];

        fields.forEach(([sel, mode]) => {
          const nEl = newPricing.querySelector(sel);
          const cEl = curPricing.querySelector(sel);
          if (!nEl || !cEl) return;
          cEl.textContent = nEl.textContent;
          if (mode === 'both') {
            cEl.style.display = nEl.style.display;
          }
        });

        /* update variant ids in forms */
        this.section.querySelectorAll('[data-ctr-main-product-v2-variant-id]').forEach((inp) => {
          inp.value = variantId;
        });

        /* update [price] tokens in checklist items */
        const newPrice = doc.querySelector('[data-ctr-main-product-v2-price-current]');
        if (newPrice) {
          const priceText = newPrice.textContent.trim();
          this.section.querySelectorAll('[data-ctr-main-product-v2-checklist-price]').forEach((span) => {
            const template = span.dataset.ctrMainProductV2ChecklistPrice;
            span.textContent = template.replace('[price]', priceText);
          });
        }

        /* update sub-box v2 subscription price and compare price */
        const newSubV2Price = doc.querySelector('[data-ctr-main-product-v2-sub-v2-price]');
        const curSubV2Price = this.section.querySelector('[data-ctr-main-product-v2-sub-v2-price]');
        if (newSubV2Price && curSubV2Price) {
          curSubV2Price.textContent = newSubV2Price.textContent;
        }

        const newSubV2Compare = doc.querySelector('[data-ctr-main-product-v2-sub-v2-compare]');
        const curSubV2Compare = this.section.querySelector('[data-ctr-main-product-v2-sub-v2-compare]');
        if (newSubV2Compare && curSubV2Compare) {
          curSubV2Compare.textContent = newSubV2Compare.textContent;
          curSubV2Compare.style.display = newSubV2Compare.style.display;
        }

        /* update [price] tokens in one-time purchase links */
        const newOtValEl = doc.querySelector('[data-ctr-main-product-v2-onetime-price-val]');
        const curOtValEl = this.section.querySelector('[data-ctr-main-product-v2-onetime-price-val]');
        if (newOtValEl && curOtValEl) {
          curOtValEl.textContent = newOtValEl.textContent;
          const otPriceText = newOtValEl.textContent.trim();
          this.section.querySelectorAll('[data-ctr-main-product-v2-one-time-price]').forEach((el) => {
            const template = el.dataset.ctrMainProductV2OneTimePrice;
            el.textContent = template.replace('[price]', otPriceText);
          });
        }
      })
      .catch(() => {});
  }

  async handleFormSubmit(form) {
    const btn = form.querySelector('[data-ctr-main-product-v2-atc]');
    const textEl = form.querySelector('[data-ctr-main-product-v2-atc-text]');
    const spinner = form.querySelector('[data-ctr-main-product-v2-spinner]');

    if (btn?.disabled) return;

    if (btn) btn.disabled = true;
    if (textEl) textEl.style.display = 'none';
    spinner?.classList.remove('hidden');

    const payload = this.buildCartPayload(form);
    if (!payload.items.length) {
      this.resetBtn(btn, textEl, spinner);
      return;
    }

    try {
      const [mainItem, ...freeItems] = payload.items;
      const mainRes = await this.addItemsToCart([mainItem]);
      if (!mainRes.ok) {
        const err = await mainRes.json();
        console.error('[ctr-main-product-v2] add main product failed', err);
        this.resetBtn(btn, textEl, spinner);
        return;
      }

      if (freeItems.length > 0) {
        const freeRes = await this.addItemsToCart(freeItems);
        if (!freeRes.ok) {
          const freeErr = await freeRes.json();
          console.error('[ctr-main-product-v2] add free products failed', freeErr);
        }
      }

      window.location.assign('/checkout');
    } catch (err) {
      console.error('[ctr-main-product-v2] add to cart error', err);
      this.resetBtn(btn, textEl, spinner);
    }
  }

  async addItemsToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    });
  }

  buildCartPayload(form) {
    const formData = new FormData(form);
    const mainVariantId = Number(formData.get('id'));
    const mainQuantity = Number(formData.get('quantity')) || 1;
    const sellingPlan = `${formData.get('selling_plan') || ''}`.trim();
    const shouldAttachFreeProduct = !form.classList.contains('ctr-main-product-v2__one-time');
    const freeVariantIds = shouldAttachFreeProduct
      ? this.getFreeVariantIdsFromDataset()
      : [];

    const items = [];
    if (mainVariantId) {
      const mainItem = {
        id: mainVariantId,
        quantity: mainQuantity,
      };
      if (sellingPlan) {
        mainItem.selling_plan = Number(sellingPlan);
      }
      items.push(mainItem);
    }

    freeVariantIds
      .filter((freeVariantId) => freeVariantId !== mainVariantId)
      .forEach((freeVariantId) => {
        const freeGiftPropertyKey = this.getFreeGiftPropertyKey();
        const shouldAddFreeGiftProperties = this.shouldAddFreeGiftProperties();
        items.push({
          id: freeVariantId,
          quantity: 1,
          properties: {
            ...(shouldAddFreeGiftProperties
              ? {
                [freeGiftPropertyKey]: 'true',
                 _ctr_main_variant_id: `${mainVariantId}` }
              : {}),
          },
        });
      });

    return { items };
  }

  getFreeVariantIdsFromDataset() {
    const raw = `${this.section.dataset.ctrMainProductV2FreeVariantIds || ''}`.trim();
    if (!raw) return [];

    const uniqueIds = new Set();
    raw.split(',').forEach((token) => {
      const id = Number(token.trim());
      if (id > 0) uniqueIds.add(id);
    });

    return [...uniqueIds];
  }

  shouldAddFreeGiftProperties() {
    return `${this.section.dataset.ctrMainProductV2FreeGiftPropertyKey || ''}`.trim() !== '';
  }

  getFreeGiftPropertyKey() {
    const key = `${this.section.dataset.ctrMainProductV2FreeGiftPropertyKey || ''}`.trim();
    return key || null;
  }

  resetBtn(btn, textEl, spinner) {
    if (btn) btn.disabled = false;
    if (textEl) textEl.style.display = '';
    spinner?.classList.add('hidden');
  }

  /* ----------------------------------------------------------------
     Video Row
     ---------------------------------------------------------------- */

  initVideoRow() {
    this.videoTracks.forEach((track) => this.initTrackDrag(track));

    /* pause all <video> elements in section except the one that just started */
    this.section.addEventListener('play', (e) => {
      if (e.target.tagName !== 'VIDEO') return;
      this.section.querySelectorAll('video').forEach((v) => {
        if (v !== e.target) { v.pause(); v.currentTime = 0; }
      });
    }, true);

    /* external-URL play buttons → modal */
    this.videoPlayBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const url = btn.dataset.videoUrl;
        const row = btn.closest('.ctr-main-product-v2__video-row');
        const modal = row?.querySelector('[data-ctr-main-product-v2-video-modal]');
        if (!modal) return;

        /* pause all inline videos while modal is open */
        this.section.querySelectorAll('video').forEach((v) => { v.pause(); v.currentTime = 0; });

        const mediaContainer = modal.querySelector('[data-ctr-main-product-v2-video-modal-media]');
        if (!mediaContainer) return;

        mediaContainer.innerHTML = this.buildVideoEmbed(url);
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        const closeModal = () => {
          modal.classList.remove('is-open');
          modal.setAttribute('aria-hidden', 'true');
          mediaContainer.innerHTML = '';
          document.body.style.overflow = '';
        };

        modal.querySelector('[data-ctr-main-product-v2-video-modal-close]')
          ?.addEventListener('click', closeModal, { once: true });
        modal.querySelector('[data-ctr-main-product-v2-video-modal-backdrop]')
          ?.addEventListener('click', closeModal, { once: true });

        const onKey = (e) => {
          if (e.key === 'Escape') { closeModal(); document.removeEventListener('keydown', onKey); }
        };
        document.addEventListener('keydown', onKey);
      });
    });
  }

  initTrackDrag(track) {
    let startX = 0;
    let startScrollLeft = 0;
    let isDragging = false;
    let hasMoved = false;

    const onDown = (clientX) => {
      isDragging = true;
      hasMoved = false;
      startX = clientX;
      startScrollLeft = track.scrollLeft;
      track.style.cursor = 'grabbing';
      track.style.userSelect = 'none';
    };

    const onMove = (clientX) => {
      if (!isDragging) return;
      const dx = clientX - startX;
      if (Math.abs(dx) > 4) hasMoved = true;
      track.scrollLeft = startScrollLeft - dx;
    };

    const onUp = () => {
      isDragging = false;
      track.style.cursor = '';
      track.style.userSelect = '';
    };

    /* mouse */
    track.addEventListener('mousedown', (e) => onDown(e.clientX));
    window.addEventListener('mousemove', (e) => { if (isDragging) onMove(e.clientX); });
    window.addEventListener('mouseup', onUp);

    /* touch */
    track.addEventListener('touchstart', (e) => onDown(e.touches[0].clientX), { passive: true });
    track.addEventListener('touchmove', (e) => onMove(e.touches[0].clientX), { passive: true });
    track.addEventListener('touchend', onUp);

    /* prevent clicks firing on cards when drag occurred */
    track.addEventListener('click', (e) => {
      if (hasMoved) e.stopPropagation();
    }, true);
  }

  /* ----------------------------------------------------------------
     Kaching Bundles price block sync — keeps the top price block
     in sync with the selected bundle price / compare price
     ---------------------------------------------------------------- */

  initKachingPriceBlockSync() {
    const priceSelector     = this.section.dataset.ctrKachingPrice?.trim();
    const compareSelector   = this.section.dataset.ctrKachingCompare?.trim();
    const configuredSelector = this.section.dataset.ctrKachingContainer?.trim();

    const curPrice   = this.section.querySelector('[data-ctr-main-product-v2-price-current]');
    const curCompare = this.section.querySelector('[data-ctr-main-product-v2-price-compare]');
    if (!curPrice) return;

    /* Resolve the Kaching container — try multiple selectors in order */
    const findContainer = () => {
      if (configuredSelector) return document.querySelector(configuredSelector);
      /* Try the custom element first, then fall back to Shopify app block and
         any element with kaching-bundles class within the section */
      return (
        document.querySelector('kaching-bundle') ||
        this.section.querySelector('[id*="kaching"], [class*="kaching-bundles__tier"], [class*="kaching-bundles__offer"], [class*="kaching-bundle"]') ||
        this.section.querySelector('.shopify-app-block')
      );
    };

    /* Given a click/mutation target, find the bundle option card within container */
    const findCard = (el, container) => {
      let node = el;
      while (node && node !== container) {
        /* Stop at an element that looks like a bundle option card */
        if (
          node.parentElement === container ||
          /tier|offer|option|bundle|deal/i.test(node.className || '')
        ) return node;
        node = node.parentElement;
      }
      return null;
    };

    /* Extract first $XX.XX value from element text */
    const extractMoney = (el) => {
      const m = (el?.textContent || '').match(/\$[\d,]+\.?\d*/);
      return m ? m[0] : null;
    };

    /* Read price/compare from a card element */
    const readPrice = (card) =>
      extractMoney(card?.querySelector('[class*="price"]:not([class*="compare"]):not([class*="original"])') || card);

    const readCompare = (card) => {
      if (!card) return null;
      /* 1. Try explicit compare/original/strikethrough elements */
      const specificEl = card.querySelector(
        '[class*="compare"], [class*="original"], [class*="was"], s, del, strike'
      );
      if (specificEl) {
        const val = extractMoney(specificEl);
        if (val) return val;
      }
      /* 2. Fallback: pick the largest money value in the card —
            compare prices are always the bigger (crossed-out) number */
      const all = (card.textContent || '').match(/\$[\d,]+\.?\d*/g);
      if (all && all.length >= 2) {
        const currentNum = parseFloat((readPrice(card) || '0').replace(/[$,]/g, ''));
        const bigger = all
          .map(p => ({ raw: p, num: parseFloat(p.replace(/[$,]/g, '')) }))
          .filter(p => p.num > currentNum);
        if (bigger.length) return bigger[0].raw;
      }
      return null;
    };

    const writePrice = (price, compare) => {
      if (!price) return;
      curPrice.textContent = price;
      if (curCompare) {
        curCompare.textContent = compare || '';
        curCompare.style.display = compare ? '' : 'none';
      }
    };

    /* Sync using explicit selectors (set in Theme Editor) */
    const syncFromSelectors = () => {
      const priceEl = priceSelector ? document.querySelector(priceSelector) : null;
      if (!priceEl) return false;
      const compareEl = compareSelector ? document.querySelector(compareSelector) : null;
      writePrice(priceEl.textContent.trim(), compareEl?.textContent.trim());
      return true;
    };

    const attachToContainer = (container) => {
      /* On click: read price from the specific clicked card */
      container.addEventListener('click', (e) => {
        setTimeout(() => {
          if (syncFromSelectors()) return;
          const card = findCard(e.target, container);
          writePrice(readPrice(card), readCompare(card));
        }, 80);
      });

      /* On any class/attribute mutation: find the changed card and read its price */
      new MutationObserver((mutations) => {
        if (syncFromSelectors()) return;
        for (const m of mutations) {
          if (['class', 'aria-selected', 'data-selected', 'style'].includes(m.attributeName)) {
            const card = findCard(m.target, container);
            const price = readPrice(card);
            if (price) { writePrice(price, readCompare(card)); return; }
          }
        }
      }).observe(container, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-selected', 'data-selected', 'style'],
      });

      /* Initial sync on load — prefer the pre-selected card, fall back to first */
      if (!syncFromSelectors()) {
        const selectedCard =
          container.querySelector('[class*="selected"], [aria-selected="true"], [data-selected]') ||
          container.firstElementChild;
        writePrice(readPrice(selectedCard), readCompare(selectedCard));
      }
    };

    const tryAttach = () => {
      const container = findContainer();
      if (container) { attachToContainer(container); return true; }
      return false;
    };

    if (!tryAttach()) {
      const observer = new MutationObserver(() => {
        if (tryAttach()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ----------------------------------------------------------------
     Kaching Bundles Buy Once — move after the ATC form so it sits
     in the same position as the original OTP block button
     ---------------------------------------------------------------- */

  initKachingBuyOnceReposition() {
    const SELECTOR = '.kaching-bundles__subscriptions--link';

    const reposition = () => {
      const kachingEl = document.querySelector(SELECTOR);
      if (!kachingEl) return;

      const atcBtn = this.section.querySelector('.ctr-main-product-v2__atc-btn');
      if (!atcBtn) return;

      const atcForm = atcBtn.closest('form');
      if (!atcForm || atcForm.contains(kachingEl)) return;

      atcForm.after(kachingEl);
    };

    if (document.querySelector(SELECTOR)) {
      reposition();
    } else {
      const observer = new MutationObserver(() => {
        if (document.querySelector(SELECTOR)) {
          observer.disconnect();
          reposition();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ----------------------------------------------------------------
     OTP price sync — watches a Kaching Bundles price element and
     keeps the Buy Once link text in sync when set via block setting
     ---------------------------------------------------------------- */

  initOtpPriceSync() {
    const otpForm = this.section.querySelector('[data-ctr-otp-price-source]');
    if (!otpForm) return;

    const selector = otpForm.dataset.ctrOtpPriceSource.trim();
    if (!selector) return;

    const syncPrice = (sourceEl) => {
      const raw = sourceEl.textContent.trim();
      if (!raw) return;

      const priceValEl = this.section.querySelector('[data-ctr-main-product-v2-onetime-price-val]');
      if (priceValEl) priceValEl.textContent = raw;

      this.section.querySelectorAll('[data-ctr-main-product-v2-one-time-price]').forEach((el) => {
        const template = el.dataset.ctrMainProductV2OneTimePrice;
        el.textContent = template.replace('[price]', raw);
      });
    };

    const attach = () => {
      const sourceEl = document.querySelector(selector);
      if (!sourceEl) return;

      syncPrice(sourceEl);

      new MutationObserver(() => syncPrice(sourceEl)).observe(sourceEl, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    };

    if (document.querySelector(selector)) {
      attach();
    } else {
      const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
          observer.disconnect();
          attach();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  buildVideoEmbed(url) {
    if (!url) return '';
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (ytMatch) {
      return `<iframe src="https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
    }
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `<iframe src="https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
    }
    if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)) {
      return `<video src="${url}" autoplay controls playsinline></video>`;
    }
    return `<iframe src="${url}" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
  }
}

/* ---- Boot ---- */

function initCtrMainProductV2() {
  document.querySelectorAll('[data-ctr-main-product-v2-section]').forEach((section) => {
    new CtrMainProductV2(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCtrMainProductV2);
} else {
  initCtrMainProductV2();
}

/* ---- Theme editor ---- */

document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('[data-ctr-main-product-v2-section]');
  if (section) new CtrMainProductV2(section);
});

document.addEventListener('shopify:block:select', (e) => {
  const accordion = e.target.closest('[data-ctr-main-product-v2-accordion]');
  if (!accordion) return;
  if (!accordion.classList.contains('is-open')) {
    accordion.querySelector('[data-ctr-main-product-v2-accordion-trigger]')?.click();
  }
});

document.addEventListener('shopify:block:deselect', (e) => {
  const accordion = e.target.closest('[data-ctr-main-product-v2-accordion]');
  if (!accordion) return;
  if (accordion.classList.contains('is-open')) {
    accordion.querySelector('[data-ctr-main-product-v2-accordion-trigger]')?.click();
  }
});

window.CtrMainProductV2 = CtrMainProductV2;

} // end if (!window.CtrMainProductV2)

/* Reset all ATC buttons when the page is restored from bfcache (browser back navigation) */
window.addEventListener('pageshow', (e) => {
  if (!e.persisted) return;
  document.querySelectorAll('[data-ctr-main-product-v2-atc]').forEach((btn) => {
    btn.disabled = false;
    const textEl = btn.querySelector('[data-ctr-main-product-v2-atc-text]');
    const spinner = btn.querySelector('[data-ctr-main-product-v2-spinner]');
    if (textEl) textEl.style.display = '';
    spinner?.classList.add('hidden');
  });
});
